import { Popover, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { DriftStatus } from '@/types'
import clsx from 'clsx'

/**
 * DriftRefinePopover — secondary filters (status / from / to) tucked behind
 * a small "Refine" button on the queue-table head row. Severity slicing
 * (the common case) lives directly in the qt-head as a chip rail; this
 * popover holds the rare-case knobs so the table doesn't need a separate
 * filter card above it.
 *
 * The legacy default-export `DriftFilters` is intentionally removed —
 * `DriftManagementTab` no longer renders it. Anyone still importing it
 * should switch to `DriftRefinePopover` rendered inside the table head.
 */

export interface DriftFilterValues {
  tenantId: string
  severity: string
  status: string
  fromDate?: string
  toDate?: string
}

interface Props {
  filters: DriftFilterValues
  onFilterChange: (filters: DriftFilterValues) => void
}

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: DriftStatus.New, label: 'New' },
  { value: DriftStatus.Pending, label: 'Pending' },
  { value: DriftStatus.PendingApproval, label: 'Pending approval' },
  { value: DriftStatus.Approved, label: 'Approved' },
  { value: DriftStatus.AwaitingRemediation, label: 'Awaiting remediation' },
  { value: DriftStatus.Rejected, label: 'Rejected' },
  { value: DriftStatus.Remediated, label: 'Remediated' },
  { value: DriftStatus.Ignored, label: 'Ignored' },
]

// Counts only the secondary fields the popover owns.
// Severity sits in the chip rail outside this component, so it's excluded.
function countActiveRefinements(filters: DriftFilterValues): number {
  let n = 0
  if (filters.status) n += 1
  if (filters.fromDate) n += 1
  if (filters.toDate) n += 1
  return n
}

export default function DriftRefinePopover({ filters, onFilterChange }: Props) {
  const activeCount = countActiveRefinements(filters)

  const updateFilter = (key: keyof DriftFilterValues, value: string) => {
    onFilterChange({ ...filters, [key]: value })
  }

  const clearAll = () => {
    onFilterChange({
      ...filters,
      status: '',
      fromDate: '',
      toDate: '',
    })
  }

  const setRange = (days: number | null) => {
    if (days == null) {
      onFilterChange({ ...filters, fromDate: '', toDate: '' })
      return
    }
    const today = new Date()
    const from = new Date(today.getTime() - days * 24 * 60 * 60 * 1000)
    onFilterChange({
      ...filters,
      fromDate: from.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0],
    })
  }

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            className={clsx(
              'inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5',
              'text-[10px] font-bold uppercase tracking-[0.16em]',
              'transition-colors duration-150',
              'bg-transparent',
              open
                ? 'border-[color:var(--color-navy,#212938)] text-[color:var(--color-navy,#212938)]'
                : 'border-[color:var(--color-rule,#E6E1D5)] text-[color:var(--color-mute,#6B7280)] hover:border-[color:var(--color-navy,#212938)] hover:text-[color:var(--color-navy,#212938)]',
            )}
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              aria-hidden="true"
            >
              <path d="M3 6h18M7 12h10M11 18h2" />
            </svg>
            <span>Refine</span>
            {activeCount > 0 && (
              <span
                className="ml-0.5 inline-flex items-center justify-center rounded-full px-1.5 py-px font-mono text-[9px] tabular-nums"
                style={{
                  background: 'var(--color-primary-500, #76D69D)',
                  color: 'var(--color-navy, #212938)',
                }}
              >
                {activeCount}
              </span>
            )}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              className={clsx(
                'absolute right-0 z-30 mt-2 w-80 origin-top-right',
                'rounded-2xl border bg-[color:var(--color-paper,#fff)]',
                'border-[color:var(--color-rule,#E6E1D5)]',
                'shadow-[0_1px_2px_rgba(33,41,56,0.06),0_24px_60px_-10px_rgba(33,41,56,0.18)]',
                'p-5',
              )}
            >
              <div className="mb-4 flex items-baseline justify-between">
                <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[color:var(--color-mute,#6B7280)]">
                  Refine
                </span>
                {activeCount > 0 && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--color-mute,#6B7280)] hover:text-[color:var(--color-navy,#212938)]"
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-mute,#6B7280)]">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => updateFilter('status', e.target.value)}
                    className={clsx(
                      'w-full rounded-full border px-3 py-1.5 text-xs font-medium',
                      'bg-[color:var(--color-paper-warm,#FBF8F2)]',
                      'border-[color:var(--color-rule,#E6E1D5)]',
                      'text-[color:var(--color-navy,#212938)]',
                      'focus:outline-none focus:border-[color:var(--color-navy,#212938)]',
                    )}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-mute,#6B7280)]">
                      From
                    </label>
                    <input
                      type="date"
                      value={filters.fromDate || ''}
                      onChange={(e) => updateFilter('fromDate', e.target.value)}
                      className={clsx(
                        'w-full rounded-full border px-3 py-1.5 text-xs font-medium',
                        'bg-[color:var(--color-paper-warm,#FBF8F2)]',
                        'border-[color:var(--color-rule,#E6E1D5)]',
                        'text-[color:var(--color-navy,#212938)]',
                        'focus:outline-none focus:border-[color:var(--color-navy,#212938)]',
                      )}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.16em] text-[color:var(--color-mute,#6B7280)]">
                      To
                    </label>
                    <input
                      type="date"
                      value={filters.toDate || ''}
                      onChange={(e) => updateFilter('toDate', e.target.value)}
                      className={clsx(
                        'w-full rounded-full border px-3 py-1.5 text-xs font-medium',
                        'bg-[color:var(--color-paper-warm,#FBF8F2)]',
                        'border-[color:var(--color-rule,#E6E1D5)]',
                        'text-[color:var(--color-navy,#212938)]',
                        'focus:outline-none focus:border-[color:var(--color-navy,#212938)]',
                      )}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 pt-1">
                  {[
                    { label: 'All time', days: null as number | null },
                    { label: '7 days', days: 7 },
                    { label: '30 days', days: 30 },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      onClick={() => setRange(preset.days)}
                      className={clsx(
                        'rounded-full border px-2.5 py-1',
                        'text-[9px] font-bold uppercase tracking-[0.14em]',
                        'border-[color:var(--color-rule,#E6E1D5)]',
                        'bg-[color:var(--color-paper,#fff)]',
                        'text-[color:var(--color-mute,#6B7280)]',
                        'hover:border-[color:var(--color-navy,#212938)] hover:text-[color:var(--color-navy,#212938)]',
                      )}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  )
}
